import { Box, Typography, useTheme } from '@mui/material';
import React, { ReactNode } from 'react';

import { hexToRGBA } from '../BarChart';
import {
  getPageHeaderIconComponent,
  type PageHeaderIconType,
  type PageHeaderIconVariant,
} from './pageHeaderIcons';

const ICON_SIZE = 36;
const ICON_FONT_SIZE = 'small';

export interface PageHeaderTitleSectionProps {
  iconType?: PageHeaderIconType;
  iconVariant?: PageHeaderIconVariant;
  icon?: ReactNode;
  title?: string;
  subtitle?: string;
  children?: ReactNode;
}

const PageHeaderTitleSection: React.FC<PageHeaderTitleSectionProps> = ({
  iconType,
  iconVariant = 'primary',
  icon,
  title,
  subtitle,
  children,
}) => {
  const theme = useTheme();

  const resolvedIcon: ReactNode =
    icon ??
    (iconType != null
      ? (() => {
          const IconComponent = getPageHeaderIconComponent(iconType);
          return (
            <IconComponent fontSize={ICON_FONT_SIZE} color={iconVariant} />
          );
        })()
      : null);

  const getIconBackgroundColor = () => {
    try {
      const themeColor = theme.palette.primary;
      if (
        themeColor &&
        typeof themeColor === 'object' &&
        'main' in themeColor
      ) {
        return hexToRGBA(themeColor.main, 0.12);
      }
    } catch {
      // fallback
    }
    return hexToRGBA(theme.palette.primary.main, 0.12);
  };

  const hasCustomContent = children != null;
  const hasTitleContent = title != null && title !== '';

  if (!hasCustomContent && !hasTitleContent) {
    return null;
  }

  if (hasCustomContent) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minHeight: 40,
        }}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 1.25,
        minHeight: ICON_SIZE,
      }}
    >
      {resolvedIcon != null && (
        <Box
          sx={{
            width: ICON_SIZE,
            height: ICON_SIZE,
            flexShrink: 0,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: getIconBackgroundColor(),
          }}
        >
          {resolvedIcon}
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          gap: 0.25,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 500,
            color: 'text.primary',
            fontSize: '1rem',
            lineHeight: 1.2,
            margin: 0,
          }}
        >
          {title}
        </Typography>
        {subtitle != null && subtitle !== '' && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: '0.8125rem',
              lineHeight: 1.2,
              margin: 0,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PageHeaderTitleSection;
